import { useEffect, useMemo, useRef } from 'react'
import { useLazyGetStores } from '../../graphQL/queries/getStores'
import { datadogRum } from '@datadog/browser-rum'
import type {
  IGetStoresResponse,
  IGetStoresVariables,
  IStore,
} from '../../graphQL/queries/getStores/types'
import type { IContainers } from '../../interfaces/config'
import { setOrderDistributor } from '../../redux/slices/orders'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import { reportOrdersBackgroundError } from './ordersHookHelpers'

const FUNCTION_NAME = 'useOrderDistributor'

const ORDER_DETAIL_PANEL_IMPORT_ROUTE = 'OrderDetailPanel'

/**
 * The RUM action emitted when a `storeCode` on an order resolves to no depot.
 *
 * An **action**, not an error, and the distinction is deliberate twice over. A
 * lookup that answers cleanly with an empty page has not failed — there is
 * nothing to attach a stack to — and `datadogRum.addError` would also drag in
 * the `startView` its orders helper performs. See
 * `reportOrdersBackgroundError` for why a background report must not move the
 * view boundary.
 * @internal
 */
export const DISTRIBUTOR_UNRESOLVED_ACTION = 'order_distributor_unresolved'

/**
 * The first store in a `getStores` page carrying a usable name, or undefined.
 *
 * **Returns the store, not the name, and that is a provenance decision.** The
 * pair the slice holds is `{ code, name }`, and taking the code from the
 * request variable while taking the name from the response would mean the two
 * halves came from different places and agreed only by author discipline. They
 * now come from one server object, checked against the code that was asked
 * for. The check is what makes it matter rather than merely tidy: if the
 * server-side filter were ever dropped or loosened, `getStores` returns page
 * one of *all* stores, and a cinnamon that read only the name would
 * confidently draw an arbitrary depot — strictly worse than the raw code this
 * feature refuses to show, because a wrong name reads as a right one.
 *
 * A whitespace-only name is treated as no name, matching the library, which
 * normalises with `String(value ?? '').trim()` before deciding whether to draw
 * the row. Filtering here rather than downstream is what lets the slice hold
 * "resolved" and "not resolved" as the only two states.
 *
 * **The nullable-entry handling is defensive, not live.** The wrapper, the
 * list and its entries are all nullable in the generated types, but the
 * deployed schema declares `Store.code` and `Store.name` as `String!`, and
 * `config/apolloClient.ts` sets no `errorPolicy`, so Apollo's default
 * `'none'` discards `data` wholesale if a non-null field ever resolved null —
 * the response would arrive as a failure, not as a list with a hole in it. The
 * only branch that fires in production is the empty array. Keep the guards;
 * do not read them as evidence that a null entry has been seen.
 *
 * "First" is "the one" because the filter is an exact match on a code that is
 * unique in practice. That is a statement about tenants' data rather than
 * about a database constraint — the `Store` schema declares no unique index on
 * `code` — so if a tenant ever did store two, this picks one arbitrarily but
 * stably, and the code check below still passes.
 * @internal
 */
export const firstStore = (data?: IGetStoresResponse): IStore | undefined => {
  const stores = data?.getStores?.stores
  if (!Array.isArray(stores)) return undefined
  return (
    stores.find((store) => (store?.name ?? '').trim().length > 0) ?? undefined
  )
}

/**
 * Whether this screen's authored config switches the distributor row off.
 *
 * The gate on the *lookup*, and deliberately not a gate on the row. The row
 * keeps the opt-out semantics of its three siblings (`payment`, `delivery`,
 * `address`): `OrderDetailPanel`'s own test is
 * `fieldsToDisplay?.[field] !== false`, so absent config still draws it. All
 * this removes is the wasted request a storefront that has said `false` would
 * otherwise pay for on every Order Detail view — no new config key, no new
 * concept, and no divergence from the siblings.
 *
 * **The asymmetry is the whole design.** Only an explicit `false` suppresses.
 * Absent config, an absent `fieldsToDisplay`, an absent panel, a value that is
 * not a boolean — every one of those still shows the row, so every one of them
 * must still issue the request. A gate that suppressed on anything short of an
 * explicit `true` would silently take the feature away from the storefronts
 * that never configured anything, which is most of them.
 *
 * **A structured read rather than the coarse `JSON.stringify` substring probe
 * `containerHasAlternativeProducts` uses.** The two are asking different
 * questions and failing a false positive differently. That one asks "is this
 * carousel configured at all", and a false positive there sends a SKU-keyed
 * query that **400s** on any tenant without the alternativeProducts add-on
 * (COM-434) — loud, and caught. This one asks "did the author write one
 * specific value", and a false positive silently takes a legitimate row's data
 * away with no request, no error and nothing to notice. A substring probe
 * would produce one the moment `"distributor":false` appeared anywhere in an
 * unrelated component's props. Loud-and-wrong and silent-and-wrong do not
 * warrant the same probe.
 *
 * **The walk cannot see a modal, and the permissive default is what makes that
 * correct.** `ModalContainer` resolves its own tree through
 * `getCinnamonPropsContainer`, so a panel authored inside a modal receives its
 * injected props and renders — while this scan, which reads only
 * header/body/footer, finds no panel at all. `!panels.length → false` is
 * therefore the answer that keeps such a storefront working: it fetches, and
 * the row draws. A storefront that authored the panel in a modal *and* set
 * `distributor: false` would pay for a request it does not use, which is the
 * cost this gate exists to remove — and is the one case where it does not.
 *
 * All three sections are scanned, since the panel may be authored in the
 * header or footer as easily as the body, and `every` over a non-empty list is
 * what makes two panels behave sensibly: one of them still drawing the row is
 * enough to need the name.
 *
 * The props read here are the config-time-resolved ones — `BaseContainer`
 * hands `OrderDetail` the same container object `Default` renders from, and
 * `processConfig.getComponentsProps` has already merged any `defaultProps`
 * preset into them. There is no `OrderDetailPanel` preset today, so in practice
 * this reads exactly what the tenant authored.
 * @internal
 */
export const isDistributorSuppressed = (container?: IContainers): boolean => {
  const panels = [container?.header, container?.body, container?.footer]
    .flatMap((section) => section?.components ?? [])
    .filter(
      (component) =>
        component?.importRoute === ORDER_DETAIL_PANEL_IMPORT_ROUTE,
    )

  if (!panels.length) return false

  return panels.every(
    (panel) =>
      (panel.props?.fieldsToDisplay as Record<string, unknown> | undefined)
        ?.distributor === false,
  )
}

/**
 * Resolves the selected order's `storeCode` to its depot name, for Order
 * Detail's distributor row (COM-889).
 *
 * ## Why this is a hook of its own
 *
 * `getOrderDetailPanelProps` is a pure synchronous function of `RootState`,
 * and the panel's props are injected through it (`PANEL_PROP_INJECTORS` in
 * `useCinnamonProps`). A `getStores` round trip cannot happen there, so the
 * resolved name has to reach the injector *through the store*: something has
 * to fetch it and dispatch it. This is that something.
 *
 * The shape follows `useAlternativeProducts` +
 * `useAlternativeProductsPdpTrigger`, which is this repo's existing answer to
 * exactly this problem — a query whose input is not the route but a value
 * that arrives asynchronously, fetched from an effect and written to a slice
 * that the render path reads. Merged into one hook here rather than two. An
 * earlier revision justified that by saying the recommendations fetch has
 * callers besides its trigger; it does not — `useAlternativeProducts` has
 * exactly one call site, which is the trigger. The honest reason is narrower:
 * there is one trigger and one consumer here and no reason yet to separate
 * them, and splitting on the day a second caller appears costs nothing.
 *
 * ## Why not inside `useGetOrderDetail`
 *
 * That was the obvious alternative and it is worse on three counts.
 *
 * It owns a **poll chain** whose bookkeeping is per-instance `useRef` state,
 * and every hazard documented there — supersession tokens, one scheduled
 * timer, the stale-config ref, `lastOrderIdRef` — exists to keep exactly one
 * chain running against one order. A second, differently-shaped request in the
 * same hook would have to opt out of all of it, and the next reader would have
 * to work out which of those mechanisms applied to which request.
 *
 * The **trigger is different**. `fetchOrderDetail` is keyed on the route's
 * order id; this is keyed on a field of the *response*. Folding it in would
 * mean deciding, on every poll tick's success path, whether the store code had
 * changed — a comparison against a value that hook has no other reason to
 * remember.
 *
 * And the **failure semantics are opposite**. A failed order read is a state
 * the shopper is shown (`error`, `notFound`, `sessionEnded`, or a preserved
 * order plus a `pollError`). A failed depot lookup is not a state at all: the
 * row is absent, exactly as it is while the lookup is in flight and exactly as
 * it is for an order with no `storeCode`. Nothing about it belongs in a
 * function whose job is to decide what the screen says went wrong.
 *
 * ## Mounted from `useOrderDetail`
 *
 * The Order Detail screen's single behaviour owner, beside
 * `useOrderConfirmationConfigGuards`. That matters for the reason the poll
 * cares about instance count: mounted twice, this would issue the lookup
 * twice. It is far more benign than a duplicated poll — an idempotent read,
 * deduplicated by Apollo's cache, writing the same value — but it is still
 * a reason not to call this from an `EventManager` handler or a second
 * template.
 *
 * ## In flight, and on failure
 *
 * Nothing renders. `selectedDistributor` stays as it is, the code comparison
 * in `selectOrderDistributor` finds no match, and the panel draws no row —
 * no skeleton, no spinner, no placeholder, and never the raw `storeCode`. The
 * visible consequence is that the row **appears late**, one round trip after
 * the order: the order is not held back for a secondary lookup. A failure
 * makes that state permanent for the order rather than changing it, which is
 * why there is no retry here and no error dispatch.
 *
 * **On screen the four unhappy paths are one state; in Datadog they are
 * three, and that asymmetry is deliberate.** A request that failed is an
 * error, a code that matched nothing is an action, and an order with no
 * `storeCode` is neither — it never reaches the request. Only "still in
 * flight" is silent. Without that split the whole feature would be
 * unobservable: every one of these is an HTTP 200 or an absence, so the only
 * detector left is a human noticing a row that is not there.
 *
 * ## Caching and repeat lookups
 *
 * Two mechanisms, and they overlap — which is worth stating plainly because
 * an earlier revision of this docstring claimed the dependency array was
 * load-bearing and the cache was not. Measured, the reverse is closer to the
 * truth for *network* traffic:
 *
 * - the **dependency array** stops the effect re-running. Poll ticks replace
 *   `selected` several times a minute and leave `storeCode` identical, so the
 *   effect body — and the `getStoresFn` call in it — runs once per code;
 * - **`cache-first`** stops a second `getStoresFn` call reaching the network
 *   at all, since the variables are identical.
 *
 * Each alone suppresses the repeat *request*, so neither is load-bearing for
 * request count on its own and removing either is invisible to a test that
 * counts network operations. `does not look the same code up again on a poll
 * tick` pins the deps property directly, by counting calls into the query hook
 * rather than operations on the link; `sends a second request when the cache is
 * bypassed` is the control that shows the two are separable. Do not replace
 * either with a `seen.length` assertion — that is the shape that measures the
 * conjunction and reports it as the part you meant.
 *
 * What the cache genuinely adds on its own is the **cross-mount** saving:
 * leaving an order and coming back, or opening a second order from the same
 * depot, issues no request because the effect does re-run there.
 *
 * ## Why it takes the container
 *
 * To skip the lookup entirely for a storefront that has switched the row off —
 * see `isDistributorSuppressed`. `BaseContainer` hands `OrderDetail` the very
 * container `Default` renders from, and `useOrderDetail` passes it through, so
 * this reads the same authored props the panel will. It is the shape
 * `useAlternativeProductsPdpTrigger` already uses for the same reason: a fetch
 * that only makes sense when the component consuming it is configured.
 */
export const useOrderDistributor = (container?: IContainers) => {
  const dispatch = useAppDispatch()
  const storefrontName = useAppSelector((state) => state.defaultSlice.storeName)
  /**
   * Selected as a primitive, not as `selected`.
   *
   * `useAppSelector` compares by reference, so subscribing to the order object
   * would re-run this hook's render on every poll tick; subscribing to the
   * string means the effect's dependency is stable across a tick that changed
   * only the order's status.
   */
  const storeCode = useAppSelector(
    (state) => state.ordersSlice.selected?.storeCode,
  )
  /**
   * Whether the authored panel has switched the row off, memoised on the
   * container so the effect's dependency is stable across the re-renders a
   * poll causes.
   */
  const suppressed = useMemo(
    () => isDistributorSuppressed(container),
    [container],
  )

  const [getStoresFn] = useLazyGetStores()

  /**
   * Monotonic token identifying the newest lookup, as in `useGetOrderDetail`.
   *
   * An in-flight query cannot be cancelled — Apollo resolves it whatever the
   * component does next — so the check has to be taken *after* the await. Two
   * things bump it: a new code superseding an older lookup, and unmount. The
   * second is this hook siding with the read half of the screen rather than
   * the write half: discarding a late read costs the shopper nothing, where
   * `useOrderConfirmation` deliberately lets a late *write* land because its
   * outcome is owed to them.
   */
  const requestIdRef = useRef(0)

  useEffect(
    () => () => {
      requestIdRef.current += 1
    },
    [],
  )

  useEffect(() => {
    /*
     * The storefront has switched this row off, so there is nothing for a
     * depot name to be shown in and no reason to ask for one. First, because
     * it is the cheapest guard here and the only one that is about the screen
     * rather than about the data.
     */
    if (suppressed) return

    /*
     * Trimmed before the emptiness test, and sent trimmed.
     *
     * `code` is `joi.string()` on `FilterStoreInput`, which rejects `''`
     * outright, so an empty code is a guaranteed 400 rather than a lookup that
     * quietly returns nothing. A whitespace-only code is the same fact wearing
     * a disguise, and would match no store even if it were accepted.
     */
    const code = (storeCode ?? '').trim()
    if (!code || !storefrontName) return

    const requestId = (requestIdRef.current += 1)
    const variables: IGetStoresVariables = {
      storefrontName,
      // `code` alone. joi puts an `.xor` across `FilterStoreInput`'s five
      // members, so adding `isActive: true` to narrow this — the obvious
      // refinement — makes the request a 400 instead of a narrower query.
      filter: { code },
    }

    const resolve = async () => {
      try {
        const result = await getStoresFn({ variables })
        // Apollo reports a failure two ways from a lazy execute — on
        // `result.error` and as a rejection — and `useGetOrders.runQuery` and
        // `useGetOrderDetail.runQuery` both normalise them the same way.
        if (result.error) throw result.error
        if (requestIdRef.current !== requestId) return

        const store = firstStore(result.data)
        /*
         * The order named a depot and the server has none under that code.
         *
         * **Reported, because it is a dangling reference rather than a data
         * choice.** An order is only stamped with a `storeCode` where the
         * tenant's checkout resolved a depot for it (see `IOrderDetail`), so a
         * code that matches nothing means the two sides have diverged — a
         * `Store.code` format that changed (`5887` against `CEDIS-5887`), or a
         * Store collection that was never imported. The *absent* code is the
         * data choice, and that case returns before the request is made.
         *
         * An earlier revision of this comment called this "not worth
         * reporting", which left a fleet-wide misconfiguration visible to
         * nobody: the response is HTTP 200 with an empty page, so there is no
         * error, no log and no counter, and `cache-first` makes it sticky for
         * the life of the mounted client. The only detector was a human
         * noticing an absence.
         *
         * Bounded by construction rather than by a dedupe set: the effect runs
         * at most once per code per mounted screen, so this is at most one
         * action per code per Order Detail view. The `storeCode` attribute is
         * what gives the "every code is unresolvable" case a denominator —
         * without it an operator can see that a tenant is affected but not
         * whether one depot record is wrong or all of them.
         */
        if (!store) {
          datadogRum.addAction(DISTRIBUTOR_UNRESOLVED_ACTION, {
            function: FUNCTION_NAME,
            storeCode: code,
            reason: 'no-match',
          })
          return
        }
        /*
         * The server answered with a store that is not the one asked for.
         *
         * Unreachable while the filter works — an exact `code` match cannot
         * return another store — which is exactly why it is worth keeping: the
         * case it guards is the filter *not* working, where `getStores`
         * returns page one of every store and the name is arbitrary. Drawing
         * an arbitrary depot under a heading saying "Distribuidor" is worse
         * than drawing nothing, which is the judgement this whole feature
         * rests on.
         */
        if (store.code !== code) {
          datadogRum.addAction(DISTRIBUTOR_UNRESOLVED_ACTION, {
            function: FUNCTION_NAME,
            storeCode: code,
            reason: 'code-mismatch',
          })
          return
        }

        // Both halves off the same server object, so the pair cannot disagree
        // about which depot it describes. See `firstStore`.
        dispatch(setOrderDistributor({ code: store.code, name: store.name }))
      } catch (exception) {
        if (requestIdRef.current !== requestId) return
        // Not `handleOrdersFetchError`: that one starts a RUM view, and this
        // failure changes nothing on screen. See
        // `reportOrdersBackgroundError`. The code travels with the report so
        // an operator can tell a broken depot record from a broken endpoint.
        reportOrdersBackgroundError(
          exception,
          FUNCTION_NAME,
          'distributor lookup failed',
          { storeCode: code },
        )
      }
    }

    resolve()
    // `getStoresFn` is stable across renders but is left out regardless: the
    // lookup should fire when the depot changes, the storefront name arrives,
    // or the screen stops suppressing the row — and on nothing else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeCode, storefrontName, suppressed])
}

export default useOrderDistributor
